import { createAsyncThunk } from '@reduxjs/toolkit'
import { firestoreService } from '@/services/firebase/firestore.service'
import { storageService } from '@/services/firebase/storage.service'
import { NewsArticle, EventArticle } from '@/types/news.types'
import { orderBy, where } from 'firebase/firestore'
import { getColombianStartOfToday } from '@/utils/colombianTimezone'

/**
 * Interfaz para agregar artículos de noticias con archivos
 * Incluye la metadata del artículo + archivos opcionales (imagen, documento)
 */
export interface AddNewsArticleWithFilesParams {
  newsData: NewsArticle
  image?: File
  document?: File
}

export const fetchNewsArticles = createAsyncThunk(
  'news/fetchNews',
  async (_, { rejectWithValue }) => {
    try {
      // Se ordena por 'createdAt' y no por 'date': orderBy excluye los documentos que
      // no tienen el campo, y los articulos sin fecha de vencimiento deben llegar al
      // cliente. El orden por vencimiento lo aplica 'selectVisibleNewsArticles'.
      const docs = await firestoreService.getDocuments('news',[
        orderBy('createdAt', 'desc')
      ]) as NewsArticle[]
      return docs
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch news')
    }
  }
)

export const fetchNewsForAdminContent = createAsyncThunk(
  'news/fetchNewsForAdminContent',
  async (_, { rejectWithValue }) => {
    try {
      const docs = await firestoreService.getDocuments('news',[
        orderBy('createdAt', 'desc')
      ]) as NewsArticle[]
      return docs
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch news')
    }
  }
)

export const fetchEventArticles = createAsyncThunk(
  'events/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      const startOfToday = getColombianStartOfToday()
      const docs = await firestoreService.getDocuments('events', [
        where('date', '>=', startOfToday),
        orderBy('date', 'asc')
      ]) as EventArticle[]

      return docs
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch events')
    }
  }
)

export const fetchNewsImage = createAsyncThunk(
  'news/fetchNewsImage',
  async (imageName: string, { rejectWithValue }) => {
    try {
      const path = `images/${imageName}`
      const imageUrl = await storageService.getFileURL({ 
        path
      })
      return imageUrl
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch image')
    }
  }
)

export const fetchNewsDocumentUrl = createAsyncThunk(
  'documents/fetchDocumentUrl',
  async ({storagePath }: {storagePath: string}, { rejectWithValue }) => {
    try {
      const downloadUrl = await storageService.getFileURL({path: storagePath})
      return downloadUrl
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch document URL')
    }
  }
)


export const addNewsArticleWithFiles = createAsyncThunk<
  NewsArticle,
  AddNewsArticleWithFilesParams,
  { rejectValue: string }
>(
  'news/addNewsArticleWithFiles',
  async ({ newsData, image, document: doc }, { rejectWithValue }) => {
  
    try {
      // For comunicados (isNews = false), check if document with same name already exists in announcements folder
      if (newsData.isNews === false && doc !== null && doc !== undefined) {
        const checkPath = `announcements/${doc.name}`
        const fileExists = await storageService.fileExists(checkPath)

        if (fileExists) {
          return rejectWithValue(`El documento "${doc.name}" ya existe en la carpeta de comunicados. Por favor, usa un nombre diferente o elimina el archivo existente.`)
        }
      }

      // First, create the document to get the ID
      const docId = await firestoreService.addDocument('news', newsData) as string

      let finalNewsData = { ...newsData }

      // Upload image with document ID prefix
      if (image !== null && image !== undefined)  {
        try {
          const imageFileName = `${docId}_${image.name}`
          const imagePath = `images/${imageFileName}`
          await storageService.uploadFile(imagePath, image)
          finalNewsData = { ...finalNewsData, imageName: imageFileName }
        } catch (error: any) {
          // Rollback: delete the created document
          await firestoreService.deleteDocument('news', docId)
          return rejectWithValue(`Error al subir imagen: ${error.message}`)
        }
      }

      // Upload document without document ID prefix (always to announcements folder)
      if (doc !== null && doc !== undefined) {
        try {
          const docFileName = doc.name
          const docPath = `announcements/${docFileName}`
          await storageService.uploadFile(docPath, doc)
          finalNewsData = { ...finalNewsData, docName: docFileName }
        } catch (error: any) {
          // Rollback: delete uploaded image and document
          if (finalNewsData.imageName) {
            await storageService.deleteFile(`images/${finalNewsData.imageName}`).catch(() => {})
          }
          await firestoreService.deleteDocument('news', docId)
          return rejectWithValue(`Error al subir documento: ${error.message}`)
        }
      }

      // Update the document with file names if any were uploaded
      if (finalNewsData.imageName || finalNewsData.docName) {
        try {
          await firestoreService.updateDocument('news', docId, {
            imageName: finalNewsData.imageName,
            docName: finalNewsData.docName
          })
        } catch (error: any) {
          return rejectWithValue(`Error al actualizar la noticia: ${error.message}`)
        }
      }

      return { ...finalNewsData, id: docId }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add news article with files')
    }
  }
)

export const updateNewsArticleWithFiles = createAsyncThunk<
  NewsArticle,
  {
    id: string
    newsData: Partial<NewsArticle>
    image?: File
    document?: File
  },
  { rejectValue: string }
>(
  'news/updateNewsArticleWithFiles',
  async ({ id, newsData, image, document: doc }, { rejectWithValue }) => {
    try {
      
      // Get the existing article to check for old files
      const existingArticle = await firestoreService.getDocument('news', id) as NewsArticle | null

      // For comunicados (isNews = false), check if new document already exists in announcements folder
      if (existingArticle?.isNews === false && doc !== undefined) {
        const newDocFileName = doc.name

        // Only check if it's a different file than the current one
        if (existingArticle?.docName !== newDocFileName) {
          const checkPath = `announcements/${doc.name}`
          const fileExists = await storageService.fileExists(checkPath)

          if (fileExists) {
            return rejectWithValue(`El documento "${doc.name}" ya existe en la carpeta de comunicados. Por favor, usa un nombre diferente o elimina el archivo existente.`)
          }
        }
      }

      let finalUpdateData = { ...newsData }

      // Handle image update
      if (image !== undefined) {
        try {
          const newImageFileName = `${id}_${image.name}`

          // Delete old image if the filename is different
          if (existingArticle?.imageName && existingArticle.imageName !== newImageFileName) {
            await storageService.deleteFile(`images/${existingArticle.imageName}`).catch(() => {})
          }

          // Upload new image with document ID prefix
          const imagePath = `images/${newImageFileName}`
          await storageService.uploadFile(imagePath, image)
          finalUpdateData = { ...finalUpdateData, imageName: newImageFileName }
        } catch (error: any) {
          return rejectWithValue(`Error al subir imagen: ${error.message}`)
        }
      }

      // Handle document update (always to announcements folder)
      if (doc !== undefined) {
        try {
          const newDocFileName = doc.name

          // Delete old document if the filename is different
          if (existingArticle?.docName && existingArticle.docName !== newDocFileName) {
            await storageService.deleteFile(`announcements/${existingArticle.docName}`).catch(() => {})
          }

          // Upload new document without document ID prefix
          const docPath = `announcements/${newDocFileName}`
          await storageService.uploadFile(docPath, doc)
          finalUpdateData = { ...finalUpdateData, docName: newDocFileName }
        } catch (error: any) {
          return rejectWithValue(`Error al subir documento: ${error.message}`)
        }
      }

      try {
        await firestoreService.updateDocument('news', id, finalUpdateData)
        return { id, ...finalUpdateData } as NewsArticle
      } catch (error: any) {
        return rejectWithValue(`Error al actualizar la noticia: ${error.message}`)
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update news article with files')
    }
  }
)

export const addEventArticle = createAsyncThunk(
  'events/addEventArticle',
  async (payload: EventArticle, { rejectWithValue }) => {
    try {
      const docId = await firestoreService.addDocument('events', payload) as string
      return { ...payload, id: docId }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add event article')
    }
  }
)

export const updateEventArticle = createAsyncThunk(
  'events/updateEventArticle',
  async ({ id, data }: { id: string; data: Partial<EventArticle> }, { rejectWithValue }) => {
    try {
      await firestoreService.updateDocument('events', id, data)
      return { id, ...data }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update event article')
    }
  }
)

export const deleteNewsArticle = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'news/deleteNewsArticle',
  async (id: string, { rejectWithValue }) => {
    let isNews = false
    try {
      // First, get the news article to check for associated files
      const article = await firestoreService.getDocument('news', id) as NewsArticle | null
      isNews = article?.isNews || false

      if (!article) {
        return rejectWithValue('Artículo no encontrado')
      }

      // Delete associated image if exists
      if (article.imageName) {
        const imagePath = `images/${article.imageName}`
        await storageService.deleteFile(imagePath)
      }

      // Delete associated document if exists
      if (article.docName) {
        // Determine the correct folder based on isNews flag
        const docPath = `announcements/${article.docName}`
        await storageService.deleteFile(docPath)
      }

      // Finally, delete the Firestore document
      await firestoreService.deleteDocument('news', id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.message || isNews ? 'Error al eliminar la noticia' : 'Error al eliminar el comunicado')
    }
  }
)

export const deleteEventArticle = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'events/deleteEventArticle',
  async (id: string, { rejectWithValue }) => {
    try {
      // First, check if the event exists
      const event = await firestoreService.getDocument('events', id) as EventArticle | null

      if (!event) {
        return rejectWithValue('Evento no encontrado')
      }

      // Delete the Firestore document
      await firestoreService.deleteDocument('events', id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al eliminar el evento')
    }
  }
)
